import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useDomains } from '../hooks/useDomains';
import { BadgeAlert as Alert, Clock, Shield, ShieldAlert, ArrowUpRight } from 'lucide-react';
import AddDomainForm from '../components/domains/AddDomainForm';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const { currentPlan, certificateLimit } = useSubscription();
  const { domains, isLoading, addDomain } = useDomains();
  const [stats, setStats] = useState({
    total: 0,
    valid: 0,
    warning: 0,
    expired: 0,
    expiringNext30Days: 0,
  });

  // Calculate domain statistics
  useEffect(() => {
    if (!domains) return;

    const validDomains = domains.filter(d => d.status === 'valid');
    const warningDomains = domains.filter(d => d.status === 'warning');
    const expiredDomains = domains.filter(d => d.status === 'expired');
    const expiringNext30Days = domains.filter(d => d.days_remaining <= 30 && d.days_remaining > 0);

    setStats({
      total: domains.length,
      valid: validDomains.length,
      warning: warningDomains.length,
      expired: expiredDomains.length,
      expiringNext30Days: expiringNext30Days.length,
    });
  }, [domains]);

  // Add handler for AddDomainForm
  const handleAddDomain = async (domain: string) => {
    try {
      await addDomain(domain);
    } catch (err) {
      toast.error('Failed to add domain');
      throw err;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Welcome section */}
      <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome, {user?.name}!</h1>
          <p className="mt-2 text-gray-600">
            You're currently on the <span className="font-medium">{currentPlan.name}</span> plan.
          </p>
        </div>
        <div className="w-full max-w-md lg:w-96">
          <AddDomainForm
            onAddDomain={handleAddDomain}
            domainsCount={domains.length}
            domainsLimit={certificateLimit}
          />
        </div>
      </div>
      <hr />
      {/* Stats overview */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-primary-100 rounded-md p-3">
                <Clock className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Domains</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.total}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/domains" className="font-medium text-primary-600 hover:text-primary-900">
                View all domains
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-accent-100 rounded-md p-3">
                <Shield className="h-6 w-6 text-accent-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Valid Certificates</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.valid}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/domains" className="font-medium text-accent-600 hover:text-accent-900">
                View valid certificates
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-warning-100 rounded-md p-3">
                <ShieldAlert className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Expiring Soon</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.warning}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/domains" className="font-medium text-warning-600 hover:text-warning-900">
                View expiring certificates
              </Link>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0 bg-danger-100 rounded-md p-3">
                <Alert className="h-6 w-6 text-danger-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Expired Certificates</dt>
                  <dd>
                    <div className="text-lg font-medium text-gray-900">{stats.expired}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-5 py-3">
            <div className="text-sm">
              <Link to="/domains" className="font-medium text-danger-600 hover:text-danger-900">
                View expired certificates
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Expiring soon section */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Certificates Expiring Soon</h2>

        {stats.expiringNext30Days > 0 ? (
          <div className="bg-white shadow-sm overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {domains
                .filter(domain => domain.days_remaining <= 30 && domain.days_remaining > 0)
                .sort((a, b) => a.days_remaining - b.days_remaining)
                .map(domain => (
                  <li key={domain.domain}>
                    <div className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-primary-600 truncate">{domain.name}</p>
                            <div
                              className={`ml-2 flex-shrink-0 inline-block px-2 py-0.5 text-xs font-medium rounded-full ${domain.days_remaining <= 7
                                  ? 'bg-danger-100 text-danger-800'
                                  : 'bg-warning-100 text-warning-800'
                                }`}
                            >
                              {domain.days_remaining} days left
                            </div>
                          </div>
                          <div className="ml-2 flex-shrink-0 flex">
                            <a
                              href={`https://${domain.name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                            >
                              <ArrowUpRight className="h-3.5 w-3.5 mr-1" />
                              Visit
                            </a>
                          </div>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              Expires on: {domain.expiry_date}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden sm:rounded-md p-6 text-center">
            <p className="text-gray-500">No certificates are expiring in the next 30 days.</p>
            <p className="text-sm text-gray-400 mt-1">All your certificates are up to date!</p>
          </div>
        )}
      </div>

      {/* Upgrade prompt for free plan */}
      {currentPlan.id === 'free' && (
        <div className="mt-8 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-primary-800">Upgrade for more features</h3>
          <p className="mt-2 text-sm text-primary-600">
            Unlock additional features such as SMS notifications, advanced monitoring, and more domains by upgrading to our Standard or Enterprise plan.
          </p>
          <div className="mt-4">
            <Link
              to="/billing"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Upgrade Now
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;